import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Package, Plus, ArrowsClockwise, ArrowBendUpLeft, Warning, CheckCircle, XCircle,
  Clock, ArrowUUpLeft, Prohibit,
} from "@phosphor-icons/react";
import AssetPicker from "@/components/app/AssetPicker";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

/* ─── Request status badge ─────────────────────────────────────────── */
const STATUS_TONE = {
  pending:                  { label: "Pending",                tone: "bg-amber-100 text-amber-800",  Icon: Clock },
  approved:                 { label: "Approved",               tone: "bg-emerald-100 text-emerald-800", Icon: CheckCircle },
  rejected:                 { label: "Rejected",               tone: "bg-rose-100 text-rose-800",    Icon: XCircle },
  returned_for_correction:  { label: "Returned for correction",tone: "bg-blue-100 text-blue-800",    Icon: ArrowUUpLeft },
  closed:                   { label: "Closed",                 tone: "bg-zinc-200 text-zinc-700",    Icon: Prohibit },
  cancelled:                { label: "Cancelled",              tone: "bg-zinc-200 text-zinc-700",    Icon: Prohibit },
};

function RequestStatusBadge({ status }) {
  const meta = STATUS_TONE[status] || STATUS_TONE.pending;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-wider ${meta.tone}`}>
      <Icon size={11} weight="bold" />
      {meta.label}
    </span>
  );
}

/* ─── Request type labels ─────────────────────────────────────────── */
const REQ_TYPES = [
  { value: "use",          label: "Request Asset for Use",      Icon: Package },
  { value: "mobilisation", label: "Request Mobilisation",       Icon: ArrowsClockwise },
  { value: "return",       label: "Report Asset Return",        Icon: ArrowBendUpLeft },
  { value: "incident",     label: "Report Incident",            Icon: Warning },
];

function typeLabel(t) {
  return REQ_TYPES.find((x) => x.value === t)?.label || t;
}

/* ─── New-request dialog ───────────────────────────────────────────── */
function NewRequestDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("use");
  const initial = {
    asset_id: "", asset_ids: [], purpose: "", required_date: "", expected_return_date: "",
    project_id: "", project_name: "", destination: "",
    incident_nature: "", incident_details: "", incident_location: "", police_reference: "",
    notes: "",
  };
  const [form, setForm] = useState(initial);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/projects").catch(() => ({ data: [] })).then((r) => setProjects(r.data || []));
  }, [open]);

  useEffect(() => { if (!open) { setForm(initial); setType("use"); } /* eslint-disable-next-line */ }, [open]);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const addAsset = (id) => {
    if (!id || form.asset_ids.includes(id)) return;
    setForm({ ...form, asset_ids: [...form.asset_ids, id] });
  };
  const removeAsset = (id) => setForm({ ...form, asset_ids: form.asset_ids.filter((x) => x !== id) });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { request_type: type, ...form };
      // Project name auto-derive
      if (form.project_id) {
        const p = projects.find((p) => p.id === form.project_id);
        payload.project_name = p?.name || "";
      }
      // Strip empty strings to keep payload clean
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k];
      });
      await api.post("/asset-requests", payload);
      toast.success("Request submitted — waiting for approval");
      setOpen(false); setForm(initial); onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" data-testid="my-asset-new-btn"><Plus size={14} className="mr-1.5" /> New Request</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-asset-new-dialog">
        <DialogHeader>
          <DialogTitle>New asset request</DialogTitle>
          <DialogDescription>Submit a request for asset use, mobilisation, return reporting, or incident reporting. Requests are routed to the Asset Manager for approval.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {/* Type selector */}
          <div className="col-span-2">
            <Label className="mb-2 block">What do you want to do? *</Label>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {REQ_TYPES.map((rt) => {
                const Icon = rt.Icon;
                const active = type === rt.value;
                return (
                  <button
                    key={rt.value}
                    type="button"
                    onClick={() => setType(rt.value)}
                    data-testid={`my-asset-type-${rt.value}`}
                    className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left text-xs transition ${
                      active
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-blue-600" : "text-slate-500"} />
                    <span className={`font-semibold ${active ? "text-blue-900" : "text-slate-700"}`}>{rt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Asset selector */}
          {type === "mobilisation" ? (
            <div className="col-span-2">
              <Label>Assets to mobilise *</Label>
              <div className="space-y-2">
                {form.asset_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {form.asset_ids.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 font-mono-alt text-[11px] text-slate-800">
                        {id}
                        <button type="button" onClick={() => removeAsset(id)} className="text-rose-600 hover:text-rose-800">×</button>
                      </span>
                    ))}
                  </div>
                )}
                <AssetPicker value="" onChange={(id) => addAsset(id)} placeholder="Add an asset…" testid="my-asset-multi-picker" />
              </div>
              <div className="mt-1 text-[10px] text-slate-500">Pick all assets that should be deployed together.</div>
            </div>
          ) : (
            <div className="col-span-2">
              <Label>Asset *</Label>
              <AssetPicker
                value={form.asset_id}
                onChange={(id) => set("asset_id")(id)}
                status={type === "use" ? "in_store" : undefined}
                placeholder={type === "use" ? "Search available assets…" : "Search asset register…"}
                testid="my-asset-picker"
              />
              {type === "use" && (
                <div className="mt-1 text-[10px] text-slate-500">Only assets currently in store are shown.</div>
              )}
            </div>
          )}

          {/* Common fields per type */}
          {(type === "use" || type === "mobilisation") && (
            <>
              <div className="col-span-2 md:col-span-1">
                <Label>{type === "use" ? "Required from *" : "Deployment date *"}</Label>
                <Input type="date" required value={form.required_date} onChange={set("required_date")} data-testid="my-asset-required-date" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Expected return</Label>
                <Input type="date" value={form.expected_return_date} onChange={set("expected_return_date")} data-testid="my-asset-return-date" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Project (optional)</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => set("project_id")(v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Not project related —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {type === "mobilisation" && (
                <div className="col-span-2 md:col-span-1">
                  <Label>Destination *</Label>
                  <Input required value={form.destination} onChange={set("destination")} placeholder="e.g. Garowe Site, Beletweyne Camp" />
                </div>
              )}
              <div className="col-span-2">
                <Label>Purpose *</Label>
                <textarea required className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={2} value={form.purpose} onChange={set("purpose")} placeholder="Explain why you need this asset and the planned use" />
              </div>
            </>
          )}

          {type === "return" && (
            <>
              <div className="col-span-2">
                <Label>Return notes</Label>
                <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={3} value={form.purpose} onChange={set("purpose")} placeholder="Describe the condition of the asset on return, any accessories returned, etc." />
              </div>
            </>
          )}

          {type === "incident" && (
            <>
              <div className="col-span-2 md:col-span-1">
                <Label>Incident nature *</Label>
                <Select value={form.incident_nature} onValueChange={set("incident_nature")}>
                  <SelectTrigger><SelectValue placeholder="Select nature…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="loss">Loss</SelectItem>
                    <SelectItem value="theft">Theft</SelectItem>
                    <SelectItem value="damage">Damage</SelectItem>
                    <SelectItem value="malfunction">Malfunction</SelectItem>
                    <SelectItem value="unauthorized_use">Unauthorized use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Location</Label>
                <Input value={form.incident_location} onChange={set("incident_location")} placeholder="Where the incident happened" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Police reference</Label>
                <Input value={form.police_reference} onChange={set("police_reference")} placeholder="If applicable" />
              </div>
              <div className="col-span-2">
                <Label>Incident details *</Label>
                <textarea required className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={3} value={form.incident_details} onChange={set("incident_details")} placeholder="Describe what happened" />
              </div>
            </>
          )}

          <div className="col-span-2">
            <Label>Additional notes</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="Optional remarks" />
          </div>

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="my-asset-submit-btn">{busy ? "Submitting…" : "Submit Request"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main page ────────────────────────────────────────────────────── */
export default function MyAssetRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const inspector = useRecordInspector();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/asset-requests/mine");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (req) => {
    try {
      await api.post(`/asset-requests/${req.id}/cancel`);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((r) => {
    if (tab === "all") return true;
    if (tab === "open") return ["pending", "returned_for_correction"].includes(r.status);
    if (tab === "approved") return r.status === "approved";
    if (tab === "closed") return ["rejected", "closed", "cancelled"].includes(r.status);
    return true;
  });

  const tabCounts = {
    all:      items.length,
    open:     items.filter((r) => ["pending", "returned_for_correction"].includes(r.status)).length,
    approved: items.filter((r) => r.status === "approved").length,
    closed:   items.filter((r) => ["rejected", "closed", "cancelled"].includes(r.status)).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">SELF-SERVICE · DESCON/QMS/AR-005</div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">My Asset Requests</h1>
          <p className="text-sm text-slate-600">Request the use of company assets, ask for mobilisation, report returns, or report incidents.</p>
        </div>
        <NewRequestDialog onCreated={load} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="my-asset-tab-all">All <span className="ml-1.5 font-mono-alt text-[10px] text-slate-500">{tabCounts.all}</span></TabsTrigger>
          <TabsTrigger value="open" data-testid="my-asset-tab-open">Open <span className="ml-1.5 font-mono-alt text-[10px] text-amber-600">{tabCounts.open}</span></TabsTrigger>
          <TabsTrigger value="approved" data-testid="my-asset-tab-approved">Approved <span className="ml-1.5 font-mono-alt text-[10px] text-emerald-600">{tabCounts.approved}</span></TabsTrigger>
          <TabsTrigger value="closed" data-testid="my-asset-tab-closed">Closed <span className="ml-1.5 font-mono-alt text-[10px] text-zinc-500">{tabCounts.closed}</span></TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="dc-card p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="dc-card p-10 text-center" data-testid="my-asset-empty">
              <Package size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
              <div className="font-display text-base font-semibold text-slate-900">No asset requests yet</div>
              <p className="mt-1 text-sm text-slate-500">Submit your first request using the button above.</p>
            </div>
          ) : (
            <div className="dc-card overflow-hidden">
              <div className="responsive-table-wrap">
                <table className="data-table w-full min-w-[900px]" data-testid="my-asset-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Asset(s)</th>
                      <th>Required / Reported</th>
                      <th>Purpose</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr
                        key={r.id}
                        className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                        onClick={() => inspector.open(r, { title: typeLabel(r.request_type), subtitle: `${r.asset_id || (r.asset_ids || []).join(", ") || "—"} · ${r.status}` })}
                        data-testid={`my-asset-row-${r.id}`}
                      >
                        <td className="px-5 py-3 font-semibold text-slate-900">{typeLabel(r.request_type)}</td>
                        <td className="px-5 py-3 font-mono-alt text-xs">
                          {r.asset_id || (r.asset_ids || []).join(", ") || "—"}
                        </td>
                        <td className="px-5 py-3 text-xs">
                          {r.required_date ? formatDate(r.required_date) : "—"}
                          {r.expected_return_date && <span className="text-slate-400"> → {formatDate(r.expected_return_date)}</span>}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-700 max-w-xs truncate">{r.purpose || r.incident_details || "—"}</td>
                        <td className="px-5 py-3"><RequestStatusBadge status={r.status} /></td>
                        <td className="px-5 py-3 text-xs text-slate-500">{formatDateTime(r.created_at)}</td>
                        <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          {r.status === "pending" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" data-testid={`my-asset-cancel-${r.id}`}>Cancel</Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Cancel request?</AlertDialogTitle>
                                  <AlertDialogDescription>This request will be marked as cancelled. You can submit a new one later if needed.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Keep</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => cancel(r)}>Cancel request</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {r.status === "approved" && r.approver_email && (
                            <span className="font-mono-alt text-[10px] uppercase tracking-wider text-emerald-700">By {r.approver_name || r.approver_email}</span>
                          )}
                          {r.status === "rejected" && r.approver_email && (
                            <span className="font-mono-alt text-[10px] uppercase tracking-wider text-rose-700" title={r.approver_comments || ""}>Rejected</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <RecordInspector {...inspector.props} />
    </div>
  );
}
